import * as React from 'react'

import { cn, definedStyles } from './cn'

type GridSize = number | 'auto' | boolean

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  container?: boolean
  item?: boolean
  /** MUI spacing units (1 unit = 8px gap). */
  spacing?: number
  rowSpacing?: number
  columnSpacing?: number
  xs?: GridSize
  sm?: GridSize
  md?: GridSize
  lg?: GridSize
  xl?: GridSize
  component?: React.ElementType
  display?: React.CSSProperties['display']
  flexDirection?: React.CSSProperties['flexDirection']
  alignItems?: React.CSSProperties['alignItems']
  justifyContent?: React.CSSProperties['justifyContent']
  textAlign?: React.CSSProperties['textAlign']
  flexGrow?: React.CSSProperties['flexGrow']
  alignSelf?: React.CSSProperties['alignSelf']
  /** Number of columns in the container's grid system (MUI default 12). */
  columns?: number
}

/** Items read their container's column count through context (MUI semantics). */
const GridColumnsContext = React.createContext(12)

const breakpointMin: Record<string, string> = {
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1536px',
}

/**
 * Exact-fit item rule. With k items per row and gap g, each item's width is
 * P% - g*(1 - P/100); a full row then totals 100% - (k-1)*g, so rows fill
 * precisely even though spacing is implemented with flex gap (the container
 * publishes its column gap as --psgrid-gap).
 */
function itemRule(selector: string, size: number | 'auto', columns: number) {
  if (size === 'auto') {
    return `${selector}{flex:0 0 auto;max-width:none;}`
  }
  const percent = Number(((size / columns) * 100).toFixed(4))
  const gapFactor = Number((1 - percent / 100).toFixed(6))
  const width = `calc(${percent}% - var(--psgrid-gap, 0px)*${gapFactor})`
  return `${selector}{flex:0 0 ${width};max-width:${width};}`
}

function normalizeSize(
  size: GridSize | undefined,
): number | 'auto' | undefined {
  if (size === undefined || size === false) return undefined
  if (size === true || size === 'auto') return 'auto'
  return size
}

const Grid = React.forwardRef<HTMLDivElement, GridProps>(
  (
    {
      container,
      item,
      spacing = 0,
      rowSpacing,
      columnSpacing,
      xs,
      sm,
      md,
      lg,
      xl,
      component: Component = 'div',
      display,
      flexDirection,
      alignItems,
      justifyContent,
      textAlign,
      flexGrow,
      alignSelf,
      columns,
      className,
      style: styleProp,
      children,
      ...props
    },
    ref,
  ) => {
    const style = definedStyles({
      display,
      flexDirection,
      alignItems,
      justifyContent,
      textAlign,
      flexGrow,
      alignSelf,
      ...styleProp,
    })
    const id = React.useId().replace(/[^a-zA-Z0-9-]/g, '')
    const gridClass = `psgrid-${id}`
    const contextColumns = React.useContext(GridColumnsContext)

    if (container) {
      const columnGapPx = (columnSpacing ?? spacing) * 8
      return (
        <GridColumnsContext.Provider value={columns ?? 12}>
          <Component
            ref={ref}
            className={cn('flex flex-wrap', className)}
            style={definedStyles({
              // px strings: some React versions treat gap as unitless and
              // CSSOM rejects bare numbers at runtime
              gap: spacing ? `${spacing * 8}px` : undefined,
              rowGap:
                rowSpacing !== undefined ? `${rowSpacing * 8}px` : undefined,
              columnGap:
                columnSpacing !== undefined
                  ? `${columnSpacing * 8}px`
                  : undefined,
              '--psgrid-gap': `${columnGapPx}px`,
              ...style,
            })}
            {...props}
          >
            {children}
          </Component>
        </GridColumnsContext.Provider>
      )
    }

    // Build responsive flex-basis rules for this item.
    const gridColumns = columns ?? contextColumns
    const rules: string[] = []
    const base = normalizeSize(xs)
    if (base !== undefined) {
      rules.push(itemRule(`.${gridClass}`, base, gridColumns))
    }
    for (const [bp, rawSize] of Object.entries({ sm, md, lg, xl })) {
      const size = normalizeSize(rawSize)
      if (size !== undefined) {
        rules.push(
          `@media (min-width:${breakpointMin[bp]}){${itemRule(
            `.${gridClass}`,
            size,
            gridColumns,
          )}}`,
        )
      }
    }

    return (
      <>
        {rules.length > 0 && <style>{rules.join('\n')}</style>}
        <Component
          ref={ref}
          className={cn('grow-0', rules.length > 0 && gridClass, className)}
          style={style}
          {...props}
        >
          {children}
        </Component>
      </>
    )
  },
)
Grid.displayName = 'Grid'

export default Grid
